import { createNestablePublicClientApplication } from "@azure/msal-browser";
import config from "../config.js";

let pca = null;

// Initialize MSAL
export const initializeMSAL = async () => {
  if (pca) {
    return pca;
  }

  try {
    pca = await createNestablePublicClientApplication({
      auth: {
        clientId: config.MSAL_COMMANDS.auth.clientId,
        authority: config.MSAL_COMMANDS.auth.authority,
      },
    });
    return pca;
  } catch (error) {
    // console.error("Failed to initialize MSAL:", error);
    throw error;
  }
};

// Get access token for Graph API
const getAccessToken = async (scopes) => {
  if (!pca) {
    await initializeMSAL();
  }

  try {
    const tokenResult = await pca.acquireTokenSilent({
      scopes: scopes,
    });
    return tokenResult.accessToken;
  } catch (error) {
    // console.error("Failed to acquire token:", error);
    throw error;
  }
};

// Helper function to log Graph API calls
const logGraphAPICall = async (url, method, statusCode, statusMessage, requestBody, responseBody, errorMessage, duration) => {
  try {
    const { logExternalCall } = await import('./loggingService.js');
    await logExternalCall({
      method: method || 'GET',
      url: url,
      path: url.replace('https://graph.microsoft.com/v1.0', '/graph'),
      statusCode: statusCode,
      statusMessage: statusMessage,
      requestBody: requestBody,
      responseBody: responseBody,
      errorMessage: errorMessage,
      duration: duration,
    });
  } catch (error) {
    // console.error('Failed to log Graph API call:', error);
  }
};

// Graph API service
export const graphAPI = {
  /**
   * Send report email with EML attachment to the forward address (e.g. security team).
   * Used by task pane report flow (mobile/desktop) to match commands.js behavior.
   * @param {string} toEmail - Forward email address
   * @param {string} subject - Email subject
   * @param {string} body - Plain text body
   * @param {string} base64EmlData - Base64-encoded EML file content
   */
  sendReportEmailWithAttachment: async (toEmail, subject, body, base64EmlData) => {
    const startTime = Date.now();
    const url = "https://graph.microsoft.com/v1.0/me/sendMail";

    const accessToken = await getAccessToken(["Mail.Send", "openid", "profile"]);

    const emailMessage = {
      message: {
        subject: subject,
        body: {
          contentType: "Text",
          content: body,
        },
        toRecipients: [
          {
            emailAddress: {
              address: toEmail,
            },
          },
        ],
        attachments: [
          {
            "@odata.type": "#microsoft.graph.fileAttachment",
            name: "reported-email.eml",
            contentType: "message/rfc822",
            contentBytes: base64EmlData,
          },
        ],
      },
      saveToSentItems: false,
    };

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(emailMessage),
    });

    const duration = Date.now() - startTime;
    const responseText = await response.text();

    if (!response.ok) {
      await logGraphAPICall(url, "POST", response.status, response.statusText, emailMessage, responseText, responseText, duration);
      throw new Error(`Failed to send email: ${response.status} ${response.statusText} - ${responseText}`);
    }

    await logGraphAPICall(url, "POST", response.status, response.statusText, emailMessage, null, null, duration);
  },

  // Get all groups
  getGroups: async () => {
    const startTime = Date.now();
    const url = "https://graph.microsoft.com/v1.0/groups?$select=id,displayName,mailEnabled,securityEnabled";

    try {
      const accessToken = await getAccessToken([
        "Group.Read.All",
        "openid",
        "profile",
      ]);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      const duration = Date.now() - startTime;
      const responseData = await response.json();

      if (!response.ok) {
        const errorText = JSON.stringify(responseData);
        await logGraphAPICall(url, 'GET', response.status, response.statusText, null, null, errorText, duration);
        throw new Error(
          `Failed to fetch groups: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      await logGraphAPICall(url, 'GET', response.status, response.statusText, null, responseData, null, duration);
      return responseData.value || [];
    } catch (error) {
      const duration = Date.now() - startTime;
      await logGraphAPICall(url, 'GET', null, null, null, null, error.message, duration);
      // console.error("Error fetching groups:", error);
      throw error;
    }
  },

  // Get group members count
  getGroupMembers: async (groupId) => {
    try {
      const accessToken = await getAccessToken([
        "Group.Read.All",
        "GroupMember.Read.All",
        "openid",
        "profile",
      ]);

      const response = await fetch(
        `https://graph.microsoft.com/v1.0/groups/${groupId}/members/$count`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            ConsistencyLevel: "eventual",
          },
        }
      );

      if (!response.ok) {
        // If count endpoint fails, try to get members
        const membersResponse = await fetch(
          `https://graph.microsoft.com/v1.0/groups/${groupId}/members`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (membersResponse.ok) {
          const membersData = await membersResponse.json();
          return membersData.value?.length || 0;
        }

        throw new Error(`Failed to fetch group members: ${membersResponse.status}`);
      }

      const count = await response.text();
      return parseInt(count, 10) || 0;
    } catch (error) {
      // console.error("Error fetching group members:", error);
      // Return 0 if we can't get the count
      return 0;
    }
  },

  // Send awareness message to all group members
  sendAwarenessMessage: async (groupIds, template, messageData) => {
    try {
      const accessToken = await getAccessToken([
        "Mail.Send",
        "Group.Read.All",
        "GroupMember.Read.All",
        "User.Read",
        "User.Read.All",
        "openid",
        "profile",
      ]);

      // Get manual email addresses from messageData
      const manualEmails = messageData.manualEmails || [];

      // Get group members if groupIds are provided
      let groupMemberEmails = [];
      const groupIdsArray = Array.isArray(groupIds) ? groupIds : (groupIds ? [groupIds] : []);
      
      if (groupIdsArray.length > 0) {
        // Fetch members from all selected groups
        for (const groupId of groupIdsArray) {
          // Get all group members with pagination support
          let allMembers = [];
          let nextLink = `https://graph.microsoft.com/v1.0/groups/${groupId}/members/microsoft.graph.user?$select=id,mail,userPrincipalName`;

          while (nextLink) {
            const membersResponse = await fetch(nextLink, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            });

            if (!membersResponse.ok) {
              const errorText = await membersResponse.text();
              // console.warn(`Failed to fetch members for group ${groupId}: ${membersResponse.status} ${membersResponse.statusText}`);
              // Continue with other groups even if one fails
              break;
            }

            const membersData = await membersResponse.json();
            allMembers = allMembers.concat(membersData.value || []);

            // Check for pagination
            nextLink = membersData["@odata.nextLink"] || null;
          }

          // Extract email addresses from members and add to groupMemberEmails
          // Use Set to avoid duplicates if user is in multiple groups
          const groupEmails = allMembers
            .map((member) => member.mail || member.userPrincipalName)
            .filter((email) => email);
          
          groupMemberEmails = [...new Set([...groupMemberEmails, ...groupEmails])]; // Remove duplicates
        }

        // groupMemberEmails now contains all unique addresses from selected groups
      }

      // Determine TO and CC recipients based on group selection
      let toRecipients = [];
      let ccRecipients = [];

      if (groupIdsArray.length > 0 && groupMemberEmails.length > 0) {
        // Groups selected: TO = group members, CC = manual emails
        toRecipients = groupMemberEmails;
        ccRecipients = manualEmails;
      } else if (manualEmails.length > 0) {
        // No groups: TO = manual emails, CC = empty
        toRecipients = manualEmails;
        ccRecipients = [];
      } else {
        // No recipients at all
        return {
          success: false,
          message: "No recipients found. Please select at least one group or enter email addresses.",
          recipientsCount: 0,
          totalRecipients: 0,
        };
      }

      const totalRecipients = toRecipients.length + ccRecipients.length;

      // Determine if body is HTML or plain text
      const bodyContent = messageData.body || messageData.htmlBody || "";
      const isHTML = bodyContent.includes("<") && bodyContent.includes(">");
      const contentType = isHTML ? "HTML" : "Text";
      

      // Microsoft Graph API has a limit of 500 recipients per email (total of TO + CC)
      // If we have more than 500, we need to batch them
      const MAX_RECIPIENTS_PER_EMAIL = 500;
      const batches = [];
      
      // Create batches: each batch can have up to 500 total recipients
      // We need to batch TO and CC separately but keep them together in each batch
      let toIndex = 0;
      let ccIndex = 0;
      
      while (toIndex < toRecipients.length || ccIndex < ccRecipients.length) {
        const batchTo = [];
        const batchCc = [];
        let batchTotal = 0;
        
        // Add TO recipients first (up to limit)
        while (toIndex < toRecipients.length && batchTotal < MAX_RECIPIENTS_PER_EMAIL) {
          batchTo.push(toRecipients[toIndex]);
          toIndex++;
          batchTotal++;
        }
        
        // Add CC recipients if there's room
        while (ccIndex < ccRecipients.length && batchTotal < MAX_RECIPIENTS_PER_EMAIL) {
          batchCc.push(ccRecipients[ccIndex]);
          ccIndex++;
          batchTotal++;
        }
        
        if (batchTo.length > 0 || batchCc.length > 0) {
          batches.push({
            to: batchTo,
            cc: batchCc,
            total: batchTotal
          });
        }
      }

      let totalSent = 0;
      const errors = [];

      // Send emails in batches
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Ensure at least one TO recipient (some email systems require this)
        // If batch has no TO but has CC, move first CC to TO
        let finalTo = [...batch.to];
        let finalCc = [...batch.cc];
        
        if (finalTo.length === 0 && finalCc.length > 0) {
          // Move first CC to TO
          finalTo.push(finalCc.shift());
        }
        
        // Prepare the email message with TO and CC
        const emailMessage = {
          message: {
            subject: messageData.subject || "Awareness Message",
            body: {
              contentType: contentType,
              content: bodyContent,
            },
            toRecipients: finalTo.map((email) => ({
              emailAddress: {
                address: email,
              },
            })),
            ...(finalCc.length > 0 && {
              ccRecipients: finalCc.map((email) => ({
                emailAddress: {
                  address: email,
                },
              })),
            }),
          },
          saveToSentItems: true,
        };

        try {
          const batchTotalRecipients = finalTo.length + finalCc.length;
          
          // Send the email using Microsoft Graph API
          const sendUrl = "https://graph.microsoft.com/v1.0/me/sendMail";
          const sendStart = Date.now();
          const sendResponse = await fetch(sendUrl, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(emailMessage),
          });

          const sendDuration = Date.now() - sendStart;

          if (!sendResponse.ok) {
            const errorText = await sendResponse.text();
            await logGraphAPICall(sendUrl, 'POST', sendResponse.status, sendResponse.statusText, { batchSize: batchTotalRecipients }, null, errorText, sendDuration);
            const error = new Error(
              `Failed to send email batch ${batchIndex + 1}: ${sendResponse.status} ${sendResponse.statusText} - ${errorText}`
            );
            errors.push(error);
            // console.error("Error sending batch:", error);
          } else {
            await logGraphAPICall(sendUrl, 'POST', sendResponse.status, sendResponse.statusText, { batchSize: batchTotalRecipients }, { success: true, recipients: batchTotalRecipients }, null, sendDuration);
            totalSent += batchTotalRecipients;
          }
        } catch (error) {
          const sendUrl = "https://graph.microsoft.com/v1.0/me/sendMail";
          const sendDuration = 0; // Duration unknown for errors
          const batchTotalRecipients = finalTo.length + finalCc.length;
          await logGraphAPICall(sendUrl, 'POST', null, null, { batchSize: batchTotalRecipients }, null, error.message, sendDuration);
          errors.push(error);
          // console.error(`Error sending batch ${batchIndex + 1}:`, error);
        }
      }

      if (totalSent === 0 && errors.length > 0) {
        throw new Error(
          `Failed to send any emails. Errors: ${errors.map((e) => e.message).join("; ")}`
        );
      }

      if (errors.length > 0) {
        // console.warn(
         // `Sent ${totalSent} out of ${totalRecipients} emails. Some batches failed.`
        //);
      }

      return {
        success: totalSent > 0,
        message:
          totalSent === totalRecipients
            ? "Message sent successfully"
            : `Message sent to ${totalSent} out of ${totalRecipients} recipients`,
        recipientsCount: totalSent,
        totalRecipients: totalRecipients,
      };
    } catch (error) {
      // console.error("Error sending awareness message:", error);
      throw error;
    }
  },
};

export default graphAPI;

